/**
 * Copyright IBM Corp. 2021
 *
 * This source code is licensed under the IBM Public License Version 1.0 found in the
 * LICENSE file in the root directory of this source tree.
 */
export default class TriFloorPlanPlugin {
  static SPACE_ID = "spaceId";
  static EMELEMENT_PROPERTY = "tri-element-name";
  static NAME_PROPERTY = "tri-record-id";

  constructor() {
    this.handleDragStart = this.handleDragStart.bind( this );
    this.handleDragMove  = this.handleDragMove.bind( this );

    this.isEnabled = true;
    this.dragBoundingBox = null;

    this.props = {
      triFloorPlan:    null,
      floorPlanLoaded: false,
      enabled:         true,
      svgViewBox:      null,
    };
    setTimeout(() => this.pluginDidUpdate({}));
  }

  setProps(props) {
    const prevProps = this.props;
    this.props = { ...prevProps, ...props };
    this.pluginDidUpdate(prevProps);
  }

  get id() {
    return this.props.id;
  }

  get enabled()
  {
    return this.isEnabled;
  }

  set enabled( state )
  {
    this.isEnabled = state;
  }

  pluginDidUpdate(prevProps) {  /* Override by subclasses */ }

  pluginDidRemove() { /* Override by subclasses */ }

  floorPlanDidResize() { /* Override by subclasses */ }

  floorPlanDidPanOrZoom( svgViewBox ) { /* Override by subclasses */ }

  /*********************************************************************************/
  /* Drag Select
   * Common support for draging an enclosing rectangle across the floorplan with the
   * mouse.  Used for drag selection and zoom box
  /*********************************************************************************/
  /*
   * Create an element to show a enclosing rectangle on mouse drag
   */
  makeDragBox( x, y )
  {
    const dragBox = document.createElement( "div" );
    dragBox.style.top = "" + y + "px";
    dragBox.style.left = "" + x + "px";
    dragBox.style.height = "5px";
    dragBox.style.width = "5px";
    dragBox.style.position = "absolute";
    dragBox.className = "TriDragBox";
    return dragBox;
  }

    // Mouse down event
    handleDragStart( evt )
    {
        if( evt.button !== 0 ) return;
        if( this.dragBoundingBox !== null ) return;

        evt.preventDefault();

        this.dragBoundingBox = {
            offset:
            {
                x: evt.screenX - evt.offsetX,
                y: evt.screenY - evt.offsetY,
            },
            start:
            {
                x: evt.offsetX,
                y: evt.offsetY,
            },
            current: { x: 0, y: 0, }
        };

        const { triFloorPlan } = this.props;
        const { svgElement } = triFloorPlan;
        const container = svgElement.parentElement;

        this.dragBox = this.makeDragBox( evt.offsetX, evt.offsetY );
        container.appendChild( this.dragBox );
    }

    // Mouse move event
    handleDragMove( evt )
    {
        evt.preventDefault();

        const { triFloorPlan } = this.props;
        const { svgElement } = triFloorPlan;
        const container = svgElement.parentElement;

        if( !this.dragBox || !this.dragBoundingBox )
        {
            return;
        }

        this.dragBoundingBox.current.x = evt.screenX - this.dragBoundingBox.offset.x;
        this.dragBoundingBox.current.y = evt.screenY - this.dragBoundingBox.offset.y;

        // Constrain the drag rectangle to the client window
        let x = this.dragBoundingBox.current.x;
        if( x < 1 ) x = 1;  // Left edge
        if( x > container.offsetWidth - 1 ) x = container.offsetWidth - 1; // Right edge

        let y = this.dragBoundingBox.current.y;
        if( y < 1 ) y = 1;  // Top edge
        if( y > container.offsetHeight - 1 ) y = container.offsetHeight - 1; // Bottom edge

        var top, left, height, width;

        left = x > this.dragBoundingBox.start.x ? this.dragBoundingBox.start.x : x;
        width = Math.abs( x - this.dragBoundingBox.start.x );
        this.dragBox.style.left = "" + left + "px";
        this.dragBox.style.width = "" + width + "px";

        top = y > this.dragBoundingBox.start.y ? this.dragBoundingBox.start.y : y;
        height = Math.abs( y - this.dragBoundingBox.start.y );
        this.dragBox.style.top = "" + top + "px";
        this.dragBox.style.height = "" + height + "px";
    }

   zoomToBox( x, y, width, height )
    {
        const { triFloorPlan } = this.props;
        const { svgViewBox, svgViewPort } = triFloorPlan;

        let scaleX = svgViewPort.width / width;
        let scaleY = svgViewPort.height / height;

        const oldScaleRatio = svgViewBox.scaleY / svgViewBox.scaleX;

        const currentHeight = svgViewPort.height * svgViewBox.scaleY;
        const currentWidth = svgViewPort.height * svgViewBox.scaleX;

        const oldAspect = currentHeight / currentWidth;
        const newAspect = height / width;

        if( oldAspect < newAspect )
        {
            const newScaleX = scaleY / oldScaleRatio;
            const newWidth = Math.round( width / newScaleX * scaleX );
            x = x + width / 2 - newWidth / 2;
            scaleX = newScaleX;
        }
        else
        {
            const newScaleY = scaleX * oldScaleRatio;
            const newHeight = Math.round( height / newScaleY * scaleY );
            y = y + height / 2 - newHeight / 2;
            scaleY = newScaleY;
        }

        const newSvgViewBox = {
            scaleX: scaleX,
            scaleY: scaleY,
            x: x,
            y: y,
        };
        triFloorPlan.svgViewBox = newSvgViewBox;
    }

    isPointInSpace( point, bbox )
    {
        if( bbox.x > point.x ) return false;
        if( bbox.x + bbox.width < point.x ) return false;
        if( bbox.y > point.y ) return false;
        if( bbox.y + bbox.height < point.y ) return false;
        return true;
    }

    hitTest( svgPoint )
    {
        const { triFloorPlan } = this.props;
        const spaceBBoxMap = triFloorPlan.spaceBBoxMap;

        const hits = [];
        const spaces = Array.from( triFloorPlan.attachedMap.values() );
        spaces.forEach( (space) => {
            const bBox = spaceBBoxMap.get( space );
            if( bBox && this.isPointInSpace( svgPoint, bBox ) )
            {
                hits.push( space );
            }
        });

        if( hits.length === 0 ) return null;

        if( hits.length === 1 ) return hits[0];

        let hit = null;
        let area = 100000000000.0;
        hits.forEach( (space) => {
            const bBox = spaceBBoxMap.get( space );
            const a = bBox.width * bBox.height;
            if( a < area )
            {
                area = a;
                hit = space;
            }
        });

        return hit;
    }
}
